/**
 * Hook do módulo RH (#29 F1b) — /api/rh/headcount + /custo + /evolucao.
 *
 * Padrão da casa: cache module-level TTL 180s chaveado por mês (#206) +
 * startVisiblePolling (#75 — nunca setInterval cru). Os 3 endpoints são
 * buscados juntos (a tela usa os três); allSettled não é preciso — falha de
 * qualquer um vira `error` (a página mostra estado explícito, sem mock mudo).
 * SÓ agregados trafegam (PII fica no backend).
 */
import { useEffect, useState } from "react";
import { getRhCusto, getRhEvolucao, getRhHeadcount } from "../api/client";
import type {
  RhCustoResponse,
  RhEvolucaoResponse,
  RhHeadcountResponse,
} from "../api/types";
import { startVisiblePolling } from "./visiblePolling";

const REFETCH_MS = 180 * 1000;

export interface RhData {
  headcount: RhHeadcountResponse;
  custo: RhCustoResponse;
  evolucao: RhEvolucaoResponse;
}

interface CacheEntry {
  expiresAt: number;
  promise: Promise<RhData>;
}

const cache = new Map<string, CacheEntry>();

export function invalidateRhCache(): void {
  cache.clear();
}

export interface UseRhState {
  data: RhData | null;
  isLoading: boolean;
  error: Error | null;
}

export function useRh(mes?: string): UseRhState {
  const [state, setState] = useState<UseRhState>({
    data: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancel = false;
    const key = mes ?? "";

    function load() {
      const now = Date.now();
      let entry = cache.get(key);
      if (!entry || entry.expiresAt < now) {
        entry = {
          expiresAt: now + REFETCH_MS,
          promise: Promise.all([
            getRhHeadcount(mes),
            getRhCusto(mes),
            getRhEvolucao(),
          ]).then(([headcount, custo, evolucao]) => ({ headcount, custo, evolucao })),
        };
        cache.set(key, entry);
      }
      entry.promise
        .then((data) => {
          if (cancel) return;
          setState({ data, isLoading: false, error: null });
        })
        .catch((err: Error) => {
          if (cancel) return;
          cache.delete(key);
          setState({ data: null, isLoading: false, error: err });
        });
    }

    setState((s) => ({ ...s, isLoading: true }));
    load();
    const stop = startVisiblePolling(load, REFETCH_MS);
    return () => {
      cancel = true;
      stop();
    };
  }, [mes]);

  return state;
}
